import { ICardsColumn, IItem } from './types';
import { RichText } from 'prismic-reactjs';
import Arrow from '@monofront/design-system/visuals/Icons/Old/Arrow';
import ResponsiveImage from '@monofront/design-system/components/ResponsiveImage';
import PrismicLink from '@components/PrismicLink';
import { useMediaQuery } from '@monofront/business/hooks';
import CustomPrismicHeading from '@components/CustomPrismicHeading';
import withSlice from '@screens/SlicesContainer/withSlice';
import { mapCardsColumn } from './mapCardsColumn';

const CardsColumn: React.FC<ICardsColumn> = ({ items, title, titleIcon }) => {
  const isMobile = useMediaQuery(480);

  return (
    <section className="cards-column">
      <RichText
        render={title}
        htmlSerializer={CustomPrismicHeading({
          className: 'catchy-title-s',
          after: titleIcon?.dimensions ? (
            <span className="title-icon">
              <ResponsiveImage
                width={titleIcon.dimensions.width}
                height={titleIcon.dimensions.height}
                src={titleIcon.url}
                alt={titleIcon.alt}
              />
            </span>
          ) : undefined,
        })}
      />
      <div className="cards">
        {items.map((item: IItem, index) => (
          <PrismicLink key={index} link={item.cardLink}>
            <article
              className="card"
              style={{ backgroundColor: item.backgroundColor }}
            >
              <div className="image">
                <ResponsiveImage
                  src={isMobile ? item.cardImageMobile.url : item.cardImage.url}
                  alt={item.cardImage.alt}
                  layout="fill"
                  objectFit="cover"
                />
              </div>
              <div className="content">
                <RichText render={item.cardText} />
                <Arrow width={12} height={18} headDirection={'right'} />
              </div>
            </article>
          </PrismicLink>
        ))}
      </div>
      <style jsx>{`
        .cards-column :global(.catchy-title-s) {
          text-align: center;
        }

        .cards-column :global(.catchy-title-s .title-icon) {
          position: relative;
          top: 8px;
          left: 6px;
          display: inline-block;
          width: 18px;
        }

        .cards {
          margin-top: var(--space-5);
        }

        .card {
          display: flex;
          align-items: center;
          border-radius: var(--rd-2);
          margin-bottom: var(--space-5);
        }

        .content {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: var(--space-5) var(--space-4) var(--space-5) var(--space-3);
          width: 100%;
        }

        .content > :global(p) {
          font-size: var(--font-size-2);
          line-height: 1.1;
          margin-right: var(--space-3);
        }

        .content :global(svg) {
          min-width: 12px;
        }

        .image {
          min-width: 200px;
          height: 120px;
          position: relative;
        }

        .image :global(img) {
          border-radius: var(--rd-3) 0 0 var(--rd-3);
        }

        @media (max-width: 1024px) {
          .content > :global(p) {
            margin-right: var(--space-2);
            font-size: var(--font-size-2);
          }
        }

        @media (max-width: 800px) {
          .cards {
            margin-top: var(--space-4);
          }

          .card {
            margin-bottom: var(--space-4);
          }

          .image {
            min-width: 30%;
            height: 120px;
            position: relative;
          }
        }
      `}</style>
    </section>
  );
};

export default withSlice(CardsColumn, mapCardsColumn);
